<div
  class="tw--m-4 tw-flex tw-min-h-[calc(100%+2rem)] tw-w-[calc(100%+2rem)] tw-bg-color-surface-page tw-p-4 sm:tw-m-0 sm:tw--mt-2 sm:tw-min-h-[calc(100dvh_-_var(--app-menu-height,0px))] sm:tw-w-full sm:tw-p-0"
>
  <div class="tw-flex tw-h-full tw-w-full tw-flex-col">
    <!-- Summary header (hidden on mobile while a detail is open) -->
    @if (!(table.budgetToDetail() && isSmallScreen())) {
      <div class="tw-flex tw-flex-col tw-gap-3 tw-py-4 sm:tw-py-2">
        <!-- Title, metric pills and Add New. FitLevelDirective keeps them on one line for as long
             as the actual content fits, compacting one piece at a time as the row narrows (see
             headerFitLevel in the component) instead of wrapping at a fixed breakpoint - which is
             why, short of the last level, the row is nowrap and its children don't shrink. -->
        <div
          class="tw-flex tw-gap-4 sm:tw-flex-col sm:tw-items-stretch"
          [ngClass]="headerStacked() ? 'tw-flex-col tw-items-stretch' : 'tw-items-center'"
          [appFitLevel]="headerMaxFitLevel"
          [fitLevelDisabled]="isSmallScreen()"
          [fitLevelContent]="budgetSummary()"
          [(fitLevel)]="headerFitLevel"
        >
          <div
            class="tw-flex tw-items-center tw-gap-2 sm:tw-w-full sm:tw-justify-between"
            [ngClass]="headerStacked() ? 'tw-w-full tw-justify-between' : 'tw-shrink-0'"
          >
            <span class="tw-whitespace-nowrap tw-text-2xl tw-font-medium tw-text-dt-text-headings">
              {{ "budget" | i18n }}
              @if (selectedPeriod() === "weekly") {
                @let weekLabel = budgetSummary().weekLabel;
                <span class="tw-inline-flex tw-items-center tw-gap-2 tw-align-middle sm:tw-hidden">
                  <span>·</span>
                  <span class="tw-flex tw-flex-col tw-text-left tw-leading-tight">
                    <span class="tw-text-sm tw-font-semibold">{{ weekLabel.week }}</span>
                    <span class="tw-text-xs tw-font-normal tw-text-dt-text-secondary">{{
                      compactTitle() ? weekLabel.range + " · " + weekLabel.year : weekLabel.range
                    }}</span>
                  </span>
                  @if (!compactTitle()) {
                    <span>·</span>
                    <span>{{ weekLabel.year }}</span>
                  }
                </span>
              } @else if (compactTitle()) {
                @let monthLabelParts = budgetSummary().monthLabelParts;
                <!-- Compact: month over year, the same stacked shape as the weekly title -->
                <span class="tw-inline-flex tw-items-center tw-gap-2 tw-align-middle sm:tw-hidden">
                  <span>·</span>
                  <span class="tw-flex tw-flex-col tw-text-left tw-leading-tight">
                    <span class="tw-text-sm tw-font-semibold">{{ monthLabelParts.month }}</span>
                    <span class="tw-text-xs tw-font-normal tw-text-dt-text-secondary">{{
                      monthLabelParts.year
                    }}</span>
                  </span>
                </span>
              } @else {
                <span class="sm:tw-hidden"> · {{ budgetSummary().monthLabel }}</span>
              }
            </span>
            <div
              class="tw-items-center tw-gap-2 sm:tw-flex"
              [ngClass]="headerStacked() ? 'tw-flex' : 'tw-hidden'"
            >
              <!-- Stacked (mobile-style) header: plus-icon-only, aligned with the title -->
              <app-gloss-button
                class="tw-shrink-0"
                [options]="{
                  testId: 'budget-create-button-mobile',
                  text: '',
                  ariaLabel: 'createNew' | i18n,
                  fullClass:
                    'border-gradient-teal tw-shadow-elevation-card tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-color-icon-action',
                  isEnabled: true,
                  icon: 'add',
                  iconClass: 'tw-h-4 tw-w-4 tw-text-color-text-on-action',
                  onClick: createNew.bind(this),
                }"
                [fontClass]="'tw-text-color-text-on-action'"
                *appRoleScope="'GLOSS'"
              ></app-gloss-button>
              <!-- Stacked header: auto-create-budget icon-only, secondary style -->
              @if (autoBudgetPluginEnabled()) {
                <app-gloss-button
                  class="tw-shrink-0"
                  [options]="{
                    testId: 'budget-auto-create-button-mobile',
                    text: '',
                    ariaLabel: 'autoCreateBudget' | i18n,
                    fullClass:
                      'tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-color-border-subtle tw-bg-color-surface-input tw-shadow-elevation-card',
                    isEnabled: true,
                    icon: 'stars',
                    iconClass: 'tw-h-4 tw-w-4 tw-text-color-text-default',
                    onClick: autoCreateBudget.bind(this),
                  }"
                  *appRoleScope="'GLOSS'"
                ></app-gloss-button>
              }
            </div>
          </div>
          <div
            appGlossScroll
            class="tw-flex tw-gap-2"
            [ngClass]="
              isSmallScreen()
                ? 'tw-w-full tw-flex-nowrap tw-overflow-x-auto tw-pb-3'
                : headerStacked()
                  ? 'tw-flex-wrap'
                  : 'tw-shrink-0'
            "
          >
            <ng-container
              *ngTemplateOutlet="
                metricPill;
                context: {
                  key: 'total',
                  label: 'total' | i18n,
                  value: budgetSummary().total,
                  valueClass: 'tw-text-dt-text-headings',
                  suffix: coreCurrency(),
                }
              "
            ></ng-container>
            <ng-container
              *ngTemplateOutlet="
                metricPill;
                context: {
                  key: 'spent',
                  label: 'spent' | i18n,
                  value: budgetSummary().spent,
                  valueClass: 'tw-text-[var(--color-status-warning-text)]',
                  suffix: coreCurrency(),
                }
              "
            ></ng-container>
            <ng-container
              *ngTemplateOutlet="
                metricPill;
                context: {
                  key: 'remaining',
                  label: 'remaining' | i18n,
                  value: budgetSummary().remaining,
                  valueClass:
                    budgetSummary().remaining < 0
                      ? 'tw-text-dt-text-error'
                      : 'tw-text-color-status-success-text',
                  suffix: coreCurrency(),
                }
              "
            ></ng-container>
            <ng-container
              *ngTemplateOutlet="
                metricPill;
                context: {
                  key: 'days-left',
                  label: 'daysLeft' | i18n,
                  value: budgetSummary().daysLeft,
                  valueClass: 'tw-text-dt-text-headings',
                  suffix: '',
                }
              "
            ></ng-container>
          </div>

          <!-- End of the row: plus icon + text, plus-icon-only once compacted that far -->
          <div
            class="tw-ml-auto tw-shrink-0 tw-items-center tw-gap-2 sm:tw-hidden"
            [ngClass]="headerStacked() ? 'tw-hidden' : 'tw-flex'"
          >
            @if (autoBudgetPluginEnabled()) {
              <app-gloss-button
                [options]="{
                  testId: 'budget-auto-create-button-desktop',
                  text: iconOnlyCreateButton() ? '' : ('autoCreateBudget' | i18n),
                  ariaLabel: iconOnlyCreateButton() ? ('autoCreateBudget' | i18n) : '',
                  fullClass: iconOnlyCreateButton()
                    ? 'tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-solid tw-border-color-border-subtle tw-bg-color-surface-input tw-shadow-elevation-card'
                    : 'tw-flex tw-h-12 tw-items-center tw-justify-center tw-gap-2 tw-rounded-2xl tw-px-4 tw-border tw-border-solid tw-border-color-border-subtle tw-bg-color-surface-input tw-shadow-elevation-card',
                  isEnabled: true,
                  icon: 'stars',
                  iconClass: 'tw-h-4 tw-w-4 tw-text-color-text-default',
                  onClick: autoCreateBudget.bind(this),
                }"
                [fontClass]="'tw-flex-row-reverse tw-text-color-text-default'"
                *appRoleScope="'GLOSS'"
              ></app-gloss-button>
            }
            <app-gloss-button
              [options]="{
                testId: 'budget-create-button-desktop',
                text: iconOnlyCreateButton() ? '' : ('createNew' | i18n),
                ariaLabel: iconOnlyCreateButton() ? ('createNew' | i18n) : '',
                fullClass: iconOnlyCreateButton()
                  ? 'border-gradient-teal tw-shadow-elevation-card tw-flex tw-h-10 tw-w-10 tw-items-center tw-justify-center tw-rounded-full tw-bg-color-icon-action'
                  : 'border-gradient-teal tw-shadow-elevation-card tw-flex tw-h-12 tw-items-center tw-justify-center tw-gap-2 tw-rounded-2xl tw-px-4 tw-bg-color-icon-action',
                isEnabled: true,
                icon: 'add',
                iconClass: 'tw-h-4 tw-w-4 tw-text-color-text-on-action',
                onClick: createNew.bind(this),
              }"
              [fontClass]="'tw-text-color-text-on-action tw-flex-row-reverse'"
              *appRoleScope="'GLOSS'"
            ></app-gloss-button>
          </div>
        </div>

        <!-- %-used legend bar -->
        <div class="tw-flex tw-flex-col tw-gap-1">
          <div class="tw-flex tw-items-center tw-gap-3">
            <span class="tw-whitespace-nowrap tw-text-xs tw-text-dt-text-secondary">
              {{ budgetSummary().usedPct | number: "1.0-0" }}% {{ "used" | i18n }}
            </span>
            <app-budget-progress-bar
              [emptyPercent]="budgetSummary().barWidth"
              [colorClass]="statusBarClass(budgetSummary().status)"
              size="md"
            />
            <span class="tw-whitespace-nowrap tw-text-xs tw-text-dt-text-secondary sm:tw-hidden">
              {{ budgetSummary().daysLeft }} {{ "daysRemaining" | i18n }}
            </span>
          </div>
          <div class="tw-flex tw-flex-wrap tw-gap-4">
            <span class="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-dt-text-secondary">
              <span class="tw-h-2 tw-w-2 tw-rounded-full tw-bg-color-status-success-text"></span>
              <span class="sm:tw-hidden">{{ "underBudget" | i18n }}</span>
              <span class="tw-hidden tw-capitalize sm:tw-inline">{{ "under" | i18n }}</span>
            </span>
            <span class="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-dt-text-secondary">
              <span class="tw-h-2 tw-w-2 tw-rounded-full tw-bg-color-status-warning"></span>
              <span class="tw-capitalize">{{ "approaching" | i18n }}</span>
            </span>
            <span class="tw-flex tw-items-center tw-gap-1.5 tw-text-xs tw-text-dt-text-secondary">
              <span class="tw-h-2 tw-w-2 tw-rounded-full tw-bg-color-status-error-text"></span>
              <span class="sm:tw-hidden">{{ "overBudget" | i18n }}</span>
              <span class="tw-hidden tw-capitalize sm:tw-inline">{{ "over" | i18n }}</span>
            </span>
          </div>
        </div>
      </div>
    }

    <!-- Split: budget list (left) + detail (right). Layout mode is driven by isSmallScreen(),
         not a CSS breakpoint alone, so it can't disagree with the conditionals below that hide
         the list/header on the same signal (see observeSmallScreen doc in shared/breakpoints.ts). -->
    <div
      class="tw-w-full tw-flex-1 tw-min-h-0 tw-gap-2"
      [ngClass]="isSmallScreen() ? 'tw-flex' : 'tw-grid tw-grid-cols-[2fr_3fr]'"
    >
      @if (!(table.budgetToDetail() && isSmallScreen())) {
        <!-- On small screens the list panel reaches the screen edge (gutter-bleed-x) and its own
             px-2 plus the list's px-2 put the cards at the 16px page edge (#6171). -->
        <div
          id="budget-table"
          class="tw-flex tw-flex-col tw-gap-4 tw-overflow-hidden tw-rounded-2xl tw-bg-color-surface-card"
          [ngClass]="
            isSmallScreen()
              ? 'gutter-bleed-x tw-px-2 tw-pb-3 tw-pt-0'
              : 'tw-w-full tw-p-3 tw-shadow-card-elevated'
          "
        >
          <!-- Desktop only: when the list panel is too narrow for the Period select plus a usable
               search box (the 2fr column at mid widths), the search drops under the select instead
               of shrinking to a few px. Its 200px basis is what lets "Search Budget..." fit; the
               phone layout keeps its single row. -->
          <div
            class="tw-flex tw-items-center tw-gap-2 tw-px-2"
            [ngClass]="{ 'tw-flex-wrap': !isSmallScreen() }"
          >
            <app-dropdown-select
              class="tw-w-[150px] tw-shrink-0"
              data-testid="budget-period-select"
              [sort]="'date'"
              [options]="periodOptions()"
              [selectedOption]="selectedPeriod()"
              [disabledOptions]="unsupportedPeriodOptions"
              [compact]="true"
              [flat]="true"
              [controlHeightClass]="'tw-h-10'"
              [customShadowClass]="
                'tw-rounded-[12px] tw-border-[2px] tw-border-solid tw-border-transparent tw-bg-color-surface-sunken tw-px-[12px] tw-py-[7px] tw-text-[14px] tw-font-semibold tw-whitespace-nowrap'
              "
              [panelClass]="'tw-min-w-[250px] tw-mt-1.5 tw-rounded-[10px]'"
              (optionSelected)="selectPeriod($event)"
            ></app-dropdown-select>
            <div
              class="tw-relative tw-flex tw-h-12 tw-grow tw-basis-[200px] tw-items-center tw-rounded-xl tw-bg-color-surface-input tw-px-4 tw-shadow-elevation-input"
            >
              <input
                type="text"
                data-testid="budget-search-input"
                class="tw-w-full tw-cursor-text tw-border-none tw-bg-transparent tw-pr-8 focus:tw-outline-none"
                placeholder="{{ 'searchBudgets' | i18n }}"
                [ngModel]="searchText()"
                (ngModelChange)="searchText.set($event)"
              />
              <div class="search-bar-icon sm:tw-right-2 sm:tw-p-2">
                <icon [name]="'magnifier'" [size]="5"></icon>
              </div>
            </div>
          </div>

          @if (budgetListItems().length) {
            <div
              data-testid="budget-list"
              class="tw-flex tw-w-full tw-flex-col tw-gap-3 tw-px-2 tw-py-2"
            >
              @for (item of budgetListItems(); track item.view.id) {
                <div
                  role="button"
                  tabindex="0"
                  data-testid="budget-list-item"
                  class="tw-box-border tw-flex tw-w-full tw-flex-col tw-gap-3 tw-rounded-2xl tw-bg-color-surface-card tw-p-4 tw-text-left tw-transition-colors"
                  [ngClass]="{
                    'shadow-card-raised': table.budgetToDetail()?.id !== item.view.id,
                    'card-selected': table.budgetToDetail()?.id === item.view.id,
                  }"
                  (click)="table.onRowClicked(item.view)"
                  (keydown.enter)="table.onRowClicked(item.view)"
                  (keydown.space)="table.onRowClicked(item.view); $event.preventDefault()"
                >
                  <div class="tw-flex tw-w-full tw-items-center tw-justify-between tw-gap-3">
                    <div
                      class="card-selected-tile tw-flex tw-h-9 tw-w-9 tw-shrink-0 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-color-border-subtle tw-bg-color-surface-input"
                    >
                      <icon [name]="'budget'" [size]="5"></icon>
                    </div>
                    <span
                      class="card-selected-name tw-line-clamp-2 tw-flex-1 tw-text-sm tw-font-semibold tw-text-dt-text-headings"
                    >
                      {{ item.view.name }}
                    </span>
                    <span
                      class="tw-flex tw-h-7 tw-min-w-[60px] tw-shrink-0 tw-items-center tw-justify-center tw-whitespace-nowrap tw-rounded-full tw-px-3 tw-text-xs tw-font-medium"
                      [ngClass]="intervalChipClass(item.view.interval)"
                    >
                      {{ item.view.interval | i18n }}
                    </span>
                    <span
                      class="tw-shrink-0 tw-font-D-DIN-PRO tw-font-semibold tw-text-dt-text-headings"
                    >
                      {{ item.budgeted | number: "1.0-0" }}
                      <span class="tw-text-[10px] tw-font-semibold tw-text-dt-text-tertiary">{{
                        item.view.currency || coreCurrency()
                      }}</span>
                    </span>
                  </div>

                  <div class="tw-flex tw-items-center tw-gap-2">
                    <app-budget-progress-bar
                      [emptyPercent]="item.barWidth"
                      [colorClass]="statusBarClass(item.status)"
                      size="md"
                    />
                    <span
                      class="tw-whitespace-nowrap tw-text-xs tw-font-medium"
                      [ngClass]="
                        item.status === 'over'
                          ? 'tw-text-dt-text-error'
                          : 'tw-text-color-status-success-text'
                      "
                    >
                      @if (item.status === "over") {
                        {{
                          -item.remaining
                            | currency: item.view.currency || coreCurrency() : "symbol" : "1.0-0"
                        }}
                        {{ "over" | i18n }}
                      } @else {
                        {{
                          item.remaining
                            | currency: item.view.currency || coreCurrency() : "symbol" : "1.0-0"
                        }}
                        {{ "left" | i18n }}
                      }
                    </span>
                  </div>
                </div>
              }
            </div>
          } @else if (searchText().trim() && table.data()?.length) {
            <!-- Budgets exist, the search just matches none of them: not the "no budgets yet" state. -->
            <app-empty-state data-testid="budget-empty-state" titleKey="noMatchingResults" />
          } @else {
            <app-empty-state
              data-testid="budget-empty-state"
              titleKey="noBudgets"
              descriptionKey="createNewBudget"
            />
          }
        </div>
      }

      @if (table.budgetToDetail()) {
        <!-- Fixed full-bleed overlay on mobile instead of a guessed tw-h-[92%] (didn't match the
             real device height, leaving the accounts list peeking through) — top+bottom pinning
             spans the true viewport, and clearing the bottom nav is done with padding-bottom
             (reads --app-menu-height, same as manage-accounts/manage-symbols) instead of shrinking
             the box, so the panel's own background always reaches the true bottom edge. Rounding
             is dropped only in this mode since a full-screen overlay keeping rounded corners
             exposes whatever's behind it at the corners. -->
        <div
          appGlossScroll
          [ngClass]="
            isSmallScreen()
              ? 'tw-fixed tw-left-0 tw-top-0 tw-bottom-0 tw-w-full tw-overflow-auto tw-bg-color-surface-page tw-rounded-none tw-pt-3 tw-pl-3 tw-pr-3 tw-pb-[calc(var(--app-menu-height,0px)+0.75rem)] tw-z-50'
              : 'tw-sticky tw-top-4 tw-self-start tw-overflow-hidden tw-rounded-2xl tw-p-3'
          "
        >
          <app-budget-details [table]="table" />
        </div>
      } @else if (!isSmallScreen()) {
        <div
          class="tw-flex tw-w-full tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-rounded-2xl tw-p-8 tw-text-center"
        >
          <div
            class="tw-flex tw-h-12 tw-w-12 tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-color-border-subtle tw-bg-color-surface-input tw-shadow-card-elevated"
          >
            <icon [name]="'budget'" [size]="6"></icon>
          </div>
          <span class="tw-text-base tw-font-semibold tw-text-dt-text-headings">
            {{ "selectBudgetItem" | i18n }}
          </span>
          <span class="tw-max-w-xs tw-text-sm tw-text-dt-text-secondary">
            {{ "selectBudgetItemDescription" | i18n }}
          </span>
        </div>
      }
    </div>
  </div>
</div>

<ng-template
  #metricPill
  let-key="key"
  let-label="label"
  let-value="value"
  let-valueClass="valueClass"
  let-suffix="suffix"
>
  <!-- compactPills(): the mobile pill sizing, used on desktop too once the header compacts that far -->
  <div
    [attr.data-testid]="'budget-summary-' + key"
    class="tw-flex tw-shrink-0 tw-min-w-[90px] tw-flex-col tw-items-center tw-text-center tw-rounded-xl tw-bg-dt-surface-input tw-shadow-card-elevated"
    [ngClass]="compactPills() ? 'tw-px-1 tw-py-2' : 'tw-px-4 tw-py-3 sm:tw-px-1 sm:tw-py-2'"
  >
    <span
      class="tw-font-semibold tw-whitespace-nowrap"
      [ngClass]="[valueClass, compactPills() ? 'tw-text-sm' : 'tw-text-lg sm:tw-text-sm']"
    >
      {{ value | number: "1.0-0" }}
      @if (suffix) {
        <span class="tw-text-[10px] tw-font-medium tw-text-dt-text-tertiary">{{ suffix }}</span>
      }
    </span>
    <span
      class="tw-uppercase tw-text-dt-text-secondary"
      [ngClass]="
        compactPills()
          ? 'tw-whitespace-normal tw-text-[9px] tw-leading-tight'
          : 'tw-text-xs tw-whitespace-nowrap sm:tw-whitespace-normal sm:tw-text-[9px] sm:tw-leading-tight'
      "
    >
      {{ label }}
    </span>
  </div>
</ng-template>
